import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { GroupTemplateFormData } from './group-template-form-data';
import { emailRegex } from '../../../../utils/regular-expressions';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

interface StepTwoProps {
  control: Control<GroupTemplateFormData>;
  setValue: UseFormSetValue<GroupTemplateFormData>;
  errors: FieldErrors<GroupTemplateFormData>;
  watch: UseFormWatch<GroupTemplateFormData>;
}

const moderationLevelOptions = [
  { displayValue: 'Moderate all messages', value: 'MODERATE_ALL_MESSAGES' },
  {
    displayValue: 'Moderate messages by new members',
    value: 'MODERATE_NEW_MEMBERS',
  },
  {
    displayValue: 'Moderate messages by non members',
    value: 'MODERATE_NON_MEMBERS',
  },
  { displayValue: `Don't moderate messages`, value: 'MODERATE_NONE' },
];

const spamModerationLevelOptions = [
  {
    displayValue:
      'Send them to the moderation queue and send notification to moderators',
    value: 'MODERATE',
  },
  {
    displayValue:
      'Send them to the moderation queue, but do not send notification to the moderators',
    value: 'SILENTLY_MODERATE',
  },
  { displayValue: 'Immediately reject them', value: 'REJECT' },
  {
    displayValue: 'Skip the moderation queue and post to the group',
    value: 'ALLOW',
  },
];

const replyToOptions = [
  { displayValue: 'To the entire group', value: 'REPLY_TO_LIST' },
  {
    displayValue: 'To the author of the message only',
    value: 'REPLY_TO_SENDER',
  },
  { displayValue: 'To the owners of the group', value: 'REPLY_TO_OWNER' },
  {
    displayValue: 'To the managers of the group',
    value: 'REPLY_TO_MANAGERS',
  },
  {
    displayValue: 'Users decide where their replies are sent',
    value: 'REPLY_TO_IGNORE',
  },
  {
    displayValue: 'Use a custom address to send replies to',
    value: 'REPLY_TO_CUSTOM',
  },
];

export default function StepTwo({
  control,
  errors,
  setValue,
  watch,
}: StepTwoProps) {
  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
      }}
    >
      <Grid sx={{ pl: 10, pt: 4 }} container spacing={4}>
        <Grid size={{ xs: 6 }}>
          <Controller
            name="membersCanPostAsTheGroup"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label="Allow members to post as the group."
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Controller
            name="messageModerationLevel"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'messageModerationLevel',
                }}
                InputLabelProps={{
                  htmlFor: 'messageModerationLevel',
                }}
                select
                label="Moderate Messages"
                sx={{ width: 400 }}
                value={field.value || ''}
                onChange={field.onChange}
              >
                {moderationLevelOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Controller
            name="sendMessageDenyNotification"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label="Notify authors when moderators reject their posts."
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Controller
            name="defaultMessageDenyNotificationText"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'defaultMessageDenyNotificationText',
                }}
                InputLabelProps={{
                  htmlFor: 'defaultMessageDenyNotificationText',
                }}
                label="Rejection Message"
                sx={{ width: 400 }}
                rows={3}
                multiline
                value={field.value || ''}
                onChange={field.onChange}
              ></TextField>
            )}
          />
        </Grid>
        <Grid size={{ xs: 7 }}>
          <Controller
            name="spamModerationLevel"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'spamModerationLevel',
                }}
                InputLabelProps={{
                  htmlFor: 'spamModerationLevel',
                }}
                select
                label="Spam Messages"
                helperText="How should suspected spam messages be handled?"
                sx={{ width: 700 }}
                value={field.value || ''}
                onChange={field.onChange}
              >
                {spamModerationLevelOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Controller
            name="replyTo"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'replyTo',
                }}
                InputLabelProps={{
                  htmlFor: 'replyTo',
                }}
                select
                label="Post Replies"
                helperText="Replies to posts can be sent to a custom email."
                sx={{ width: 400 }}
                value={field.value || ''}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  if (e.target.value != 'REPLY_TO_CUSTOM') {
                    setValue('customReplyTo', '');
                  }
                }}
              >
                {replyToOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        {watch('replyTo') === 'REPLY_TO_CUSTOM' ? (
          <Grid size={{ xs: 6 }}>
            <Controller
              name="customReplyTo"
              control={control}
              rules={{
                validate: {
                  AddressIsValid: (value) => {
                    const emailError = !emailRegex.test(value);

                    if (emailError && watch('replyTo') === 'REPLY_TO_CUSTOM') {
                      return 'Not a valid email.';
                    }
                  },
                },
              }}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'customReplyTo',
                  }}
                  InputLabelProps={{
                    htmlFor: 'customReplyTo',
                  }}
                  label="Custom Address"
                  sx={{ width: 400 }}
                  error={!!errors.customReplyTo}
                  helperText={errors?.customReplyTo?.message || ''}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
}
